import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  ImageIcon, PlayCircle, Sparkles, CheckCircle2, AlertTriangle,
  RefreshCw, X, Wand2, Loader2, MessageSquare, Eye,
} from 'lucide-react';
import BPTextFixBatchPanel from './BPTextFixBatchPanel';
import BPVisualFixBatchPanel from './BPVisualFixBatchPanel';
import BPReverifyAllPanel from './BPReverifyAllPanel';
import BPMimeFixPanel from './BPMimeFixPanel';
import ParityStatusPanel from './ParityStatusPanel';
import CancelStuckAuditButton from './CancelStuckAuditButton';
import BPExecutorDiagnosticsPanel from './BPExecutorDiagnosticsPanel';
import BPAuditRecentRunsTable from './BPAuditRecentRunsTable';
import BPRecentWedgesPanel from './BPRecentWedgesPanel';
import { playSuccessChime, playFailureChime } from '../utils/maintenanceAudio';
import { ensureNotificationPermission, notifyOnCompletion } from '../utils/jobCompletionNotifier';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem('mosaic_session_token') || '';
  return { headers: { Authorization: `Bearer ${t}` } };
};

const overallStyle = (v) => v === 'pass'
  ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
  : v === 'warn'
  ? 'bg-amber-100 text-amber-800 border-amber-300'
  : 'bg-rose-100 text-rose-800 border-rose-300';
const riskStyle = (v) => v === 'HIGH'
  ? 'bg-rose-100 text-rose-800 border-rose-300'
  : v === 'MED'
  ? 'bg-amber-100 text-amber-800 border-amber-300'
  : 'bg-emerald-100 text-emerald-800 border-emerald-300';

const BPContentFitAudit = () => {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runStartedAt, setRunStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [filter, setFilter] = useState('fail'); // 'all' | 'fail' | 'warn' | 'pass'

  // iterative refinement dialog state
  const [active, setActive] = useState(null);   // scene result being refined
  const [prompt, setPrompt] = useState('');
  const [adjustment, setAdjustment] = useState('');
  const [history, setHistory] = useState([]);   // [{candidate_url, prompt, approved?}]
  const [candidate, setCandidate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [pw, setPw] = useState('');
  const [approving, setApproving] = useState(false);

  // v6.32.234 — current platform version (pulled from /api/platform/version)
  // so we can detect when the stored audit run is from a previous deploy
  // and surface the stale-result banner.
  const [versionInfo, setVersionInfo] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform/version`)
      .then(r => r.json()).then(d => setVersionInfo(d)).catch(() => {});
  }, []);

  const loadLast = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bp-auditor/last`, authCfg());
      setRun(data);
      if (data?.status === 'running') {
        setRunning(true);
        setRunStartedAt(data.started_at ? Date.parse(data.started_at) : Date.now());
      }
    } catch (e) {
      toast.error(`Load last run failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLast(); }, [loadLast]);

  // Elapsed-time ticker while audit is running
  useEffect(() => {
    if (!running || !runStartedAt) return;
    const i = setInterval(() => setElapsed(Math.floor((Date.now() - runStartedAt) / 1000)), 1000);
    return () => clearInterval(i);
  }, [running, runStartedAt]);

  const runAudit = async () => {
    if (running) {
      toast.info('Audit already running — please wait for it to finish.');
      return;
    }
    if (!window.confirm(
      'Run full BP content-fit audit? This calls MOSAIC content engine vision on all ~110 Big Picture scenes (~3-4 minutes, ~$1.65 of content-engine credits). You can stay on this tab or come back later — the result will load automatically.'
    )) return;
    // v6.35.7 — request desktop notification permission on this user
    // gesture so the operator gets pinged when the audit finishes even
    // if their tab is hidden / browser minimized.
    ensureNotificationPermission();
    try {
      // Kick off background job — returns immediately with a run_id
      const { data } = await axios.post(`${API}/bp-auditor/run`, {}, authCfg());
      setRun(data);  // shows the initial "running" state with progress=0
      setRunning(true);
      setRunStartedAt(Date.now());
      setElapsed(0);
      toast.info('Audit started in background — progress will update live.', { duration: 5000 });
    } catch (e) {
      toast.error(`Audit start failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Poll /last every 4s while the audit is running (summary-only = small payload)
  useEffect(() => {
    if (!running) return;
    const tick = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/bp-auditor/last?summary=true`, authCfg());
        setRun(prev => ({ ...(prev || {}), ...data }));  // keep any prior results until full load
        if (data?.status === 'complete') {
          setRunning(false);
          setRunStartedAt(null);
          const s = data.stats || {};
          // v6.34.4 — completion chime so the operator hears the run finish
          // even when their tab is in the background.
          playSuccessChime();
          // v6.35.7 — desktop notification so the operator sees it even
          // when the tab is hidden / browser minimized.
          notifyOnCompletion({
            title: 'BP Content-Fit Audit complete',
            body: `${s.pass || 0} pass / ${s.warn || 0} warn / ${s.fail || 0} fail · ${s.total || 0} scenes`,
            success: true,
          });
          toast.success(`Audit complete: ${s.pass || 0} pass / ${s.warn || 0} warn / ${s.fail || 0} fail`);
          // Fetch the full results (with per_question details) now that it's done
          try {
            const full = await axios.get(`${API}/bp-auditor/last`, authCfg());
            setRun(full.data);
          } catch (_) {}
        } else if (data?.status === 'failed') {
          setRunning(false);
          setRunStartedAt(null);
          // v6.34.4 — failure chime: distinct descending tone.
          playFailureChime();
          // v6.35.7 — failure notification persists until dismissed so
          // the operator can't miss it on their way back to the desk.
          notifyOnCompletion({
            title: 'BP Content-Fit Audit FAILED',
            body: data.error || data.failure_reason || data.phase || 'see Recent Runs for details',
            success: false,
          });
          toast.error(`Audit failed: ${data.error || 'unknown'}`);
        }
      } catch (_) {}
    }, 4000);
    return () => clearInterval(tick);
  }, [running]);

  const seedPromptFromScene = (scene) => {
    const a = scene.audit || {};
    const questions = (a.per_question || []).map((q, i) => `${i + 1}. ${q.correct_answer || '(answer)'} — ${q.evidence || ''}`).join('\n');
    return (
      `A warm, photographic-style, text-free illustration for a K-12 WIDA assessment.\n\n` +
      `GRADE BAND: ${scene.grade}\n` +
      `SCENE / CONTEXT: ${scene.context}\n\n` +
      `MUST INCLUDE VISUAL ANCHORS FOR EVERY LISTED CORRECT ANSWER:\n${questions}\n\n` +
      `PREVIOUS ISSUES TO FIX:\n${a.scene_fit_reason || ''}\n${a.suggested_fixes || ''}\n\n` +
      `HARD RULES:\n` +
      `- ABSOLUTELY NO TEXT anywhere (no letters, words, signage, posters with lettering, chalkboards with writing).\n` +
      `- Respectful, authentic cultural representation if cultural.\n` +
      `- Age-appropriate for grade ${scene.grade}.\n` +
      `- 4:3 aspect ratio.`
    );
  };

  const beginRefinement = (scene) => {
    setActive(scene);
    const initial = seedPromptFromScene(scene);
    setPrompt(initial);
    setAdjustment('');
    setHistory([]);
    setCandidate(null);
    setPw('');
  };

  const doGenerate = async () => {
    if (prompt.length < 40) { toast.error('Prompt too short'); return; }
    setGenerating(true);
    setCandidate(null);
    try {
      const { data } = await axios.post(`${API}/bp-auditor/generate`, {
        grade: active.grade, context: active.context, prompt,
      }, { ...authCfg(), timeout: 120000 });
      setCandidate(data);
      setHistory(prev => [...prev, { candidate_url: data.candidate_url, prompt, bytes: data.bytes }]);
    } catch (e) {
      toast.error(`Generate failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const doAdjust = async () => {
    if (!adjustment.trim()) { toast.error('Describe the adjustment first'); return; }
    const newPrompt =
      prompt +
      `\n\nREVISION NOTE (user feedback on previous attempt):\n${adjustment.trim()}\n` +
      `Apply this change while keeping all other anchors and rules intact. Re-emphasize the text-free rule.`;
    setPrompt(newPrompt);
    setAdjustment('');
    // kick off a regen with the appended feedback
    setGenerating(true);
    setCandidate(null);
    try {
      const { data } = await axios.post(`${API}/bp-auditor/generate`, {
        grade: active.grade, context: active.context, prompt: newPrompt,
      }, { ...authCfg(), timeout: 120000 });
      setCandidate(data);
      setHistory(prev => [...prev, { candidate_url: data.candidate_url, prompt: newPrompt, bytes: data.bytes }]);
    } catch (e) {
      toast.error(`Adjust failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const doApprove = async () => {
    if (!candidate) { toast.error('Generate a candidate first'); return; }
    if (!pw) { toast.error('Password required'); return; }
    setApproving(true);
    try {
      const { data } = await axios.post(`${API}/bp-auditor/approve`, {
        grade: active.grade, context: active.context,
        candidate_url: candidate.candidate_url, password: pw,
        note: `After ${history.length} iteration(s)`,
      }, authCfg());
      toast.success(`Approved: ${data.question_count} items repointed to ${data.new_url}`);
      setActive(null);
      setCandidate(null);
      setPw('');
      // Refresh run so the scene now shows the new image; audit stays the same until user re-runs
      await loadLast();
    } catch (e) {
      // v6.32.205: distinguish auth expiry from other failures and
      // PRESERVE the candidate state so the operator can re-login and
      // re-approve without losing 10+ minutes of refinement work.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e.message;
      if (status === 401 || /password/i.test(String(detail || ''))) {
        toast.error(
          'Session expired or password incorrect. Your refined candidate is preserved — re-enter the password and click Approve again.',
          { duration: 8000 }
        );
        // Intentionally DO NOT clear `candidate` or `history`. Only
        // clear the password input so the operator can retype it.
        setPw('');
      } else {
        toast.error(`Approve failed: ${detail}`);
      }
    } finally {
      setApproving(false);
    }
  };

  const results = run?.results || [];
  const filtered = results.filter(r => {
    if (filter === 'all') return true;
    return (r.audit?.overall || 'warn') === filter;
  });
  const stats = run?.stats || { total: 0, pass: 0, warn: 0, fail: 0, high_risk_questions: 0, scenes_with_text: 0 };

  return (
    <div className="space-y-6" data-testid="bp-audit-tab">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ImageIcon size={20} className="text-[#002FA7]" />
              <h2 className="text-xl font-bold">Big Picture Content-Fit Audit</h2>
            </div>
            <p className="text-sm text-gray-600 max-w-2xl">
              MOSAIC content engine vision inspects each Big Picture illustration against every question bound to it, checking: does the image actually contain a visual anchor for each correct answer? Any text in the image? Scene fit? Cultural accuracy? Failed scenes open an iterative refinement dialog — describe what to change, MOSAIC engine regenerates, repeat until you approve.
            </p>
            {run?.at && <p className="text-xs text-gray-500 mt-2">Last run: {run.at.slice(0, 19).replace('T', ' ')} · {stats.total} scenes audited · {run.by || 'system'}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={runAudit} disabled={running} data-testid="bp-audit-run-btn">
              {running ? <Loader2 size={14} className="mr-1 animate-spin" /> : <PlayCircle size={14} className="mr-1" />}
              {running ? `Running… ${elapsed}s` : 'Run Audit Now'}
            </Button>
          </div>
        </div>
      </div>

      {/* Running banner — prominent so the user knows the audit is in-flight */}
      {running && (
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-4 flex items-center gap-3" data-testid="bp-audit-running-banner">
          <Loader2 className="animate-spin text-indigo-600 flex-shrink-0" size={24} />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-indigo-900 text-sm">
              Audit in progress — MOSAIC content engine is inspecting {run?.progress?.total || 110} Big Picture scenes
            </div>
            <div className="text-xs text-indigo-700 mt-0.5">
              <strong>{run?.progress?.done || 0}</strong> / {run?.progress?.total || 110} scenes done
              &nbsp;· elapsed <strong>{Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}</strong>
              {/* v6.34.3 — per-scene pace + ETA so the operator can self-verify the
                  run is healthy without waiting for the "stuck" banner threshold.
                  Pace is averaged over the run; ETA extrapolates remaining work.
                  v6.34.4 — color-coded pace badge: green ≤ 40s/scene, amber 40-90s,
                  red > 90s (likely genuinely stuck). One-glance health signal. */}
              {run?.progress?.done > 0 && elapsed > 0 && (() => {
                const paceSec = elapsed / run.progress.done;
                const remaining = (run.progress.total || 110) - run.progress.done;
                const etaSec = Math.round(paceSec * remaining);
                const etaMin = Math.floor(etaSec / 60);
                const etaSecRem = etaSec % 60;
                const paceColor = paceSec <= 40
                  ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                  : paceSec <= 90
                  ? 'bg-amber-100 text-amber-800 border-amber-300'
                  : 'bg-rose-100 text-rose-800 border-rose-300';
                return (
                  <span data-testid="bp-audit-pace-eta">
                    &nbsp;·{' '}
                    <span
                      className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold border rounded ${paceColor}`}
                      data-testid="bp-audit-pace-badge"
                      data-pace-bucket={paceSec <= 40 ? 'healthy' : paceSec <= 90 ? 'slow' : 'stuck-likely'}
                    >
                      {paceSec.toFixed(1)}s/scene
                    </span>
                    &nbsp;· ETA <strong>{etaMin}:{String(etaSecRem).padStart(2, '0')}</strong>
                  </span>
                );
              })()}
              &nbsp;· you can leave this tab and come back — progress continues server-side.
            </div>
            {/* v6.34.8 — Currently working on: <scene>. Surfaces the
                oldest in-flight item the audit heartbeat ticker writes
                to bp_audit_runs.current_op_scene (via v6.34.6
                hardening). One-glance bottleneck visibility — if the
                same scene is shown for >5 min, that scene is the
                slow lane and the per-scene timeout is about to fire. */}
            {run?.current_op_scene && (
              <div className="mt-1.5 text-xs text-indigo-900 flex items-center gap-1.5"
                   data-testid="bp-audit-current-op">
                <span className="font-semibold">Currently working on:</span>
                <code className="bg-white/70 px-1.5 py-0.5 rounded border border-indigo-200">
                  {run.current_op_scene}
                </code>
                {/* v6.34.9 — per-scene elapsed timer. Computed client-side
                    from run.current_op_started_at (UTC ISO string written
                    by the audit heartbeat ticker). Color-coded same as the
                    pace badge: green ≤ 60s, amber 60-300s, red > 300s
                    (which means v6.34.6's per-scene timeout is about to
                    fire). One-glance "is this scene wedged?" signal —
                    no JSON tab required. */}
                {run.current_op_started_at && (() => {
                  const startMs = Date.parse(run.current_op_started_at);
                  if (Number.isNaN(startMs)) return null;
                  const sceneSec = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
                  const min = Math.floor(sceneSec / 60);
                  const sec = sceneSec % 60;
                  const color = sceneSec <= 60
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : sceneSec <= 300
                    ? 'bg-amber-50 border-amber-200 text-amber-800'
                    : 'bg-rose-50 border-rose-200 text-rose-800';
                  return (
                    <span
                      className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold ${color}`}
                      data-testid="bp-audit-current-op-elapsed"
                      data-scene-elapsed-bucket={sceneSec <= 60 ? 'healthy' : sceneSec <= 300 ? 'slow' : 'about-to-timeout'}
                      title={`This scene has been in flight for ${min}:${String(sec).padStart(2, '0')}. The per-scene timeout (v6.34.6) fires at 5:00.`}
                    >
                      {min}:{String(sec).padStart(2, '0')}
                    </span>
                  );
                })()}
                {run.in_flight_count > 1 && (
                  <span className="text-indigo-600">
                    (+{run.in_flight_count - 1} more in flight)
                  </span>
                )}
              </div>
            )}
            <div className="mt-2 h-1.5 bg-indigo-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-500 transition-all duration-500"
                style={{
                  width: `${run?.progress?.total
                    ? Math.min(99, (run.progress.done / run.progress.total) * 100)
                    : Math.min(95, (elapsed / 210) * 100)}%`,
                }}
              />
            </div>
            {/* v6.32.12: surface cancel option if the audit is clearly stuck.
                v6.34.3: bumped from 1800s (30 min) → 5400s (90 min). At
                ~30s/scene the current 110-scene library takes ~55 min to
                audit; the 30-min threshold was firing on healthy in-flight
                runs and painting a false-alarm "Cancel stuck audit" banner. */}
            {elapsed > 5400 && (
              <CancelStuckAuditButton onCancelled={loadLast} />
            )}
          </div>
        </div>
      )}

      {/* v6.35.5 — Recent Audit Runs + Resume support (watchdog_stale chip) */}
      <BPAuditRecentRunsTable onRunStarted={loadLast} />

      {/* v6.35.5 — Live content-engine Executor pool diagnostics + force-flush */}
      <BPExecutorDiagnosticsPanel />

      {/* v6.35.8 — Recent Wedges pattern detector */}
      <BPRecentWedgesPanel />

      {/* v6.32.234 — stale-result indicator: when the stored audit run
          was produced by a different platform version than what's now
          live, prompt the operator to re-run. Without this banner the
          v6.32.231 _encode_local_image fix would land but the operator
          would still see the pre-fix 110/110 fail-style numbers from
          the stored run and think the fix didn't work. */}
      {!running && run?.platform_version && versionInfo?.version &&
        run.platform_version !== versionInfo.version && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex items-start gap-3"
             data-testid="bp-audit-stale-banner">
          <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={22} />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-amber-900 text-sm">
              These results are from a previous deploy — re-run for accurate numbers
            </div>
            <div className="text-xs text-amber-800 mt-1 leading-relaxed">
              Last audit ran on <strong>v{run.platform_version}</strong>
              {run.completed_at && (
                <> at <strong>{new Date(run.completed_at).toLocaleString()}</strong></>
              )}
              {run.by && <> by <span className="font-mono">{run.by}</span></>}.
              The platform has since been updated to <strong>v{versionInfo.version}</strong>.
              Several fixes between those versions affect what this audit measures
              (e.g. <em>v6.32.231 fixed the URL-prefix bug that made <code>_encode_local_image</code> return
              None for every scene → all 110 scenes reported "Image file missing" → counted as fail</em>).
              Click <strong>Run Audit Now</strong> above to get the post-fix distribution.
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { k: 'Total scenes', v: stats.total, color: 'text-gray-900' },
          { k: 'Pass', v: stats.pass || 0, color: 'text-emerald-700' },
          { k: 'Warn', v: stats.warn || 0, color: 'text-amber-700' },
          { k: 'Fail', v: stats.fail || 0, color: 'text-rose-700' },
          { k: 'HIGH-risk Qs', v: stats.high_risk_questions || 0, color: 'text-rose-700' },
          { k: 'Scenes w/ text', v: stats.scenes_with_text || 0, color: 'text-rose-700' },
        ].map(s => (
          <div key={s.k} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">{s.k}</div>
            <div className={`text-2xl font-semibold mt-1 ${s.color}`}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {['fail', 'warn', 'pass', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              filter === f ? 'bg-[#002FA7] border-[#002FA7] text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            data-testid={`bp-audit-filter-${f}`}
          >
            {f === 'all' ? `All (${results.length})` : `${f[0].toUpperCase() + f.slice(1)} (${stats[f] || 0})`}
          </button>
        ))}
      </div>

      {/* Full-scope reverify+remediate sub-panel (always renders) — PRIMARY tool */}
      <BPReverifyAllPanel />

      {/* Batch text-violation fix sub-panel (only renders when text_fix_review.json exists) */}
      <BPTextFixBatchPanel />

      {/* Batch visual-anchor fix sub-panel (only renders when visual_fix_review.json exists) */}
      <BPVisualFixBatchPanel />

      {/* v6.32.11: One-click MIME cleanup — rare preflight, demoted below the primary tools */}
      <BPMimeFixPanel />


      {/* Scene rows */}
      {loading ? (
        <div className="bg-white border rounded-xl p-8 text-center text-gray-500">
          <Loader2 className="inline-block animate-spin mr-2" size={18} /> Loading…
        </div>
      ) : !run?.run_id ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center">
          <ImageIcon className="mx-auto text-gray-400 mb-3" size={36} />
          <div className="font-semibold text-gray-700">No audit has been run yet</div>
          <div className="text-sm text-gray-500 mt-1">Click "Run Audit Now" above to inspect all Big Picture scenes.</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-emerald-200 rounded-xl p-8 text-center">
          <CheckCircle2 className="mx-auto text-emerald-500 mb-2" size={32} />
          <div className="font-semibold text-emerald-800">No scenes in this filter</div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((scene, idx) => (
            <div key={`${scene.grade}|${scene.context}|${idx}`}
                 className="bg-white border border-gray-200 rounded-xl p-4 flex gap-4 flex-wrap md:flex-nowrap"
                 data-testid={`bp-audit-row-${idx}`}>
              <div className="w-32 h-24 flex-shrink-0 bg-gray-100 border rounded overflow-hidden">
                {scene.bp_image_url
                  ? <img src={scene.bp_image_url} alt={scene.context} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">no img</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge variant="outline" className="bg-gray-50">Gr {scene.grade}</Badge>
                  <span className="font-semibold truncate">{scene.context}</span>
                  <Badge className={`border ${overallStyle(scene.audit?.overall)}`} variant="outline">{scene.audit?.overall || 'warn'}</Badge>
                  {scene.audit?.text_in_image && <Badge className="bg-rose-100 text-rose-800 border border-rose-300" variant="outline">has text</Badge>}
                  <Badge variant="outline" className="text-gray-600">{scene.question_count} Qs</Badge>
                </div>
                {scene.audit?.scene_fit_reason && (
                  <p className="text-xs text-gray-600 mb-1"><strong>Scene:</strong> {scene.audit.scene_fit_reason}</p>
                )}
                {scene.audit?.suggested_fixes && (
                  <p className="text-xs text-gray-600 mb-1"><strong>Suggested fixes:</strong> {scene.audit.suggested_fixes}</p>
                )}
                <div className="mt-1 space-y-1">
                  {(scene.audit?.per_question || []).map((q, qi) => (
                    <div key={qi} className="text-[11px] flex items-start gap-2">
                      <Badge className={`${riskStyle(q.risk)} border flex-shrink-0`} variant="outline">{q.risk || '?'}</Badge>
                      <span className={`inline-block w-4 text-center ${q.anchor_present ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {q.anchor_present ? '✓' : '✗'}
                      </span>
                      <span className="text-gray-700"><strong>{q.correct_answer || '?'}</strong> — {q.evidence || ''}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0">
                <Button size="sm" onClick={() => beginRefinement(scene)} data-testid={`bp-audit-fix-${idx}`}>
                  <Wand2 size={14} className="mr-1" /> Fix Image
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(scene.bp_image_url, '_blank')} data-testid={`bp-audit-view-${idx}`}>
                  <Eye size={14} className="mr-1" /> View
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Iterative refinement dialog */}
      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="bp-audit-dialog">
          <DialogHeader>
            <DialogTitle>Refine Big Picture Image</DialogTitle>
            <DialogDescription>
              Generate a candidate, review it, describe adjustments, regenerate. When it's right, approve with your Super Admin password.
            </DialogDescription>
          </DialogHeader>

          {active && (
            <div className="space-y-4">
              <div className="bg-gray-50 border rounded p-3 text-xs space-y-1">
                <div><strong>Scene:</strong> {active.context} <Badge variant="outline" className="ml-1">Gr {active.grade}</Badge></div>
                <div><strong>Questions bound to this image:</strong> {active.question_count}</div>
                {(active.audit?.per_question || []).map((q, qi) => (
                  <div key={qi} className="text-[11px] text-gray-700 ml-4">
                    {qi + 1}. <strong>Correct:</strong> {q.correct_answer || '?'} · <span className={q.anchor_present ? 'text-emerald-600' : 'text-rose-600'}>{q.anchor_present ? 'anchor present' : 'anchor MISSING'}</span>
                  </div>
                ))}
              </div>

              {/* Current vs candidate preview */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] text-gray-500 mb-1">Current</div>
                  <img src={active.bp_image_url} alt="current" className="w-full h-56 object-cover border rounded" />
                </div>
                <div>
                  <div className="text-[11px] text-gray-500 mb-1">Candidate</div>
                  {candidate
                    ? <img src={candidate.candidate_url} alt="candidate" className="w-full h-56 object-cover border rounded" />
                    : (
                      <div className="w-full h-56 border rounded bg-gray-50 flex items-center justify-center text-xs text-gray-400">
                        {generating ? <><Loader2 className="animate-spin mr-2" size={16} /> Generating...</> : 'Click "Generate" below'}
                      </div>
                    )}
                </div>
              </div>

              {/* Iteration history strip */}
              {history.length > 1 && (
                <div className="border-t pt-2">
                  <div className="text-[11px] text-gray-500 mb-1">Iteration history ({history.length} generated)</div>
                  <div className="flex gap-2 overflow-x-auto">
                    {history.map((h, i) => (
                      <img key={i} src={h.candidate_url} alt={`v${i + 1}`}
                           className={`w-20 h-16 object-cover border rounded flex-shrink-0 cursor-pointer ${h.candidate_url === candidate?.candidate_url ? 'border-indigo-500 ring-2 ring-indigo-300' : 'border-gray-200'}`}
                           onClick={() => setCandidate({ candidate_url: h.candidate_url, bytes: h.bytes })} />
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs">Base prompt (edit freely)</Label>
                <Textarea rows={8} value={prompt} onChange={e => setPrompt(e.target.value)} className="font-mono text-xs" data-testid="bp-audit-prompt" />
              </div>

              {!candidate && (
                <Button onClick={doGenerate} disabled={generating} data-testid="bp-audit-generate-btn">
                  {generating ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />}
                  {generating ? 'Generating…' : 'Generate Candidate'}
                </Button>
              )}

              {candidate && (
                <>
                  <div className="bg-indigo-50 border border-indigo-200 rounded p-3 space-y-2">
                    <Label className="text-xs font-semibold text-indigo-900 flex items-center gap-1">
                      <MessageSquare size={12} /> What needs adjustment? (regenerates with your feedback)
                    </Label>
                    <Textarea
                      rows={3}
                      value={adjustment}
                      onChange={e => setAdjustment(e.target.value)}
                      placeholder='e.g. "The teacher is not holding the red envelope — make the giving gesture more obvious. Also remove the Chinese characters from the poster."'
                      data-testid="bp-audit-adjustment"
                    />
                    <Button variant="outline" onClick={doAdjust} disabled={generating || !adjustment.trim()} data-testid="bp-audit-adjust-btn">
                      {generating ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
                      Regenerate with adjustment
                    </Button>
                  </div>

                  <div className="border-t pt-3 space-y-2">
                    <Label className="text-xs">Super Admin password (commit approved candidate)</Label>
                    <Input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Your Super Admin password" data-testid="bp-audit-pw" />
                    <div className="text-[11px] text-gray-500">
                      Approving replaces the image on <strong>{active.question_count}</strong> question(s) bound to “{active.context}”. Writes a rollback manifest.
                    </div>
                    <Button onClick={doApprove} disabled={approving} data-testid="bp-audit-approve-btn">
                      {approving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <CheckCircle2 size={14} className="mr-1" />}
                      Approve Current Candidate
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActive(null)} data-testid="bp-audit-cancel">
              <X size={14} className="mr-1" /> Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BPContentFitAudit;
